import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users, Activity, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { findSimilarForums } from '../utils/similarForums';
import { calculateForumScore } from '../utils/forumScore';
import type { ForumBase } from '../types/forum';
import { withCacheBust } from '../utils/mediaUrl';

interface Props {
  currentForum: ForumBase;
  darkMode: boolean;
}

const RelatedForums: React.FC<Props> = ({ currentForum, darkMode }) => {
  const [relatedForums, setRelatedForums] = useState<ForumBase[]>([]);

  useEffect(() => {
    const fetchRelatedForums = async () => {
      try {
        const { data, error } = await supabase
          .from('forums')
          .select('*')
          .eq('category', currentForum.category)
          .eq('is_active', true);

        if (error) throw error;
        setRelatedForums(findSimilarForums(currentForum, data || [], 5));
      } catch (error) {
        console.error('Error fetching related forums:', error);
        setRelatedForums([]);
      }
    };

    fetchRelatedForums();
  }, [currentForum.id, currentForum.category]);

  if (relatedForums.length === 0) {
    return null;
  }

  const card = darkMode
    ? 'bg-gray-800 border-gray-700 hover:border-indigo-600'
    : 'bg-white border-gray-200 hover:border-indigo-300 shadow-sm';

  return (
    <section className="mt-8" aria-labelledby="similar-forums-heading">
      <h2
        id="similar-forums-heading"
        className={`text-xl font-semibold mb-1 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}
      >
        <Sparkles className="text-indigo-500 shrink-0" size={22} />
        {currentForum.name}&apos;a benzer forumlar
      </h2>
      <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Aynı kategoride ({currentForum.category}), benzer üye ve skor profiline sahip {relatedForums.length}{' '}
        forum
      </p>
      <div className="grid gap-3 sm:grid-cols-1">
        {relatedForums.map((forum) => (
          <Link
            key={forum.id}
            to={`/forum/${forum.forum_id}`}
            className={`block border rounded-xl p-4 transition-colors ${card}`}
          >
            <div className="flex items-center gap-4">
              {forum.logo_url && (
                <img
                  src={withCacheBust(forum.logo_url, forum.updated_at) ?? forum.logo_url}
                  alt={forum.name} 
                  className="w-12 h-12 object-contain rounded"
                />
              )}
              <div className="flex-1">
                <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {forum.name}
                </h3>
                <div className="flex flex-wrap gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="text-amber-500" size={16} />
                    <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {calculateForumScore(
                        forum.topics_count,
                        forum.posts_count,
                        forum.members_count,
                        forum.founded_year
                      ).toLocaleString('tr-TR')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Activity className="text-green-500" size={16} />
                    <span className="text-sm">{forum.posts_count.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="text-blue-500" size={16} />
                    <span className="text-sm">{forum.members_count.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedForums;